import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson, fetchCurrentUser } from '../utils/authClient.js';
import { localizePath } from '../utils/localizedRoutes.js';
import { FactGrid, formatBerlin } from './VeridionShared.jsx';

const azuriteTemplate = {
  available: false,
  configured: false,
  error: '',
  generated_at: null,
  summary: {
    model_groups: 0,
    online_models: 0,
    models_with_artifacts: 0,
    blob_total: 0,
    latest_upload_at: null,
  },
  models: [],
};

const AzuritePage = () => {
  const { t, currentLang } = useTranslation();
  const [me, setMe] = useState(null);
  const [azurite, setAzurite] = useState(azuriteTemplate);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());
  const formatTimestamp = formatBerlin;
  const formatBytes = (value) => {
    const size = Number(value || 0);
    if (!Number.isFinite(size) || size <= 0) return '-';
    if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${size} B`;
  };
  const smtmStateLabel = (state) => t.admin.smtmStateLabels?.[state] || state || '-';
  const smtmStateClass = (state) => {
    if (['available', 'online'].includes(state)) return 'is-ok';
    if (['warning', 'offline'].includes(state)) return 'is-warning';
    return 'is-missing';
  };
  const smtmIssueLabel = (issue) => t.admin.smtmIssueLabels?.[issue] || issue;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const currentUser = await fetchCurrentUser();
        setMe(currentUser.user);
        if (!currentUser.user?.roles?.includes('admin')) {
          setLoading(false);
          return;
        }
        const payload = await requestJson('/api/admin/azurite');
        setAzurite({ ...azuriteTemplate, ...payload, error: '' });
        setError('');
      } catch (requestError) {
        setError(requestError.code || 'SMTM_AZURITE_UNAVAILABLE');
        setAzurite(azuriteTemplate);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const filteredModels = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return azurite.models || [];
    return (azurite.models || []).filter((entry) => `${entry.display_name} ${entry.name} ${entry.status}`.toLowerCase().includes(needle));
  }, [azurite.models, filter]);
  const pageError = error || azurite.error;

  if (loading) {
    return <main className="site-main"><div className="site-card p-6">{t.auth.validating}</div></main>;
  }

  if (!me || !me.roles.includes('admin')) {
    return <main className="site-main"><div className="site-card p-6">{t.auth.errors.adminRequired}</div></main>;
  }

  return (
    <main className="site-main admin-dashboard-main space-y-6">
      <section className="site-card p-6 space-y-4 admin-hero-card">
        <div className="admin-section-head">
          <div>
            <div className="editorial-kicker">{t.admin.adminKicker}</div>
            <h1 className="admin-hero-title mt-3">{t.admin.azuriteTitle}</h1>
            <p className="site-copy">{t.admin.azuriteSubtitle}</p>
          </div>
          <div className="text-sm text-text-secondary admin-smtm-status-meta">
            <div>{t.admin.smtmGeneratedAt}: {formatTimestamp(azurite.generated_at)}</div>
            <div>{t.admin.smtmLatestModelUpload}: {formatTimestamp(azurite.summary?.latest_upload_at)}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <a className="homepage-button-secondary" href={localizePath('/admin', currentLang)}>{t.nav?.admin}</a>
        </div>
        {pageError ? <div className="homepage-muted-panel p-4 text-sm text-error">{pageError}</div> : null}
      </section>

      <section className="site-card admin-panel admin-section is-open">
        <FactGrid items={[
        [t.admin.smtmOnlineModels, formatInteger(azurite.summary?.online_models || 0)],
        [t.admin.azuriteModelGroups, formatInteger(azurite.summary?.model_groups || 0)],
        [t.admin.smtmModelsWithArtifacts, formatInteger(azurite.summary?.models_with_artifacts || 0)],
        [t.admin.azuriteBlobArtifacts, formatInteger(azurite.summary?.blob_total || 0)],
        ]} />
      </section>

      <section className="site-card p-6 space-y-4 admin-panel admin-span-12">
        <div className="admin-section-head">
          <div>
            <h2 className="text-2xl font-black">{t.admin.azuriteTitle}</h2>
            <p className="site-copy">{t.admin.azuriteSubtitle}</p>
          </div>
          <input
            className="form-input admin-inline-input"
            placeholder={t.admin.azuriteSearchPlaceholder}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>

        <div className="admin-table-shell">
          <div className="admin-table-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t.admin.smtmInstrument}</th>
                  <th>{t.admin.smtmModelPublish}</th>
                  <th>{t.admin.smtmArtifacts}</th>
                  <th>{t.admin.smtmLatestModelUpload}</th>
                  <th>{t.admin.azuriteFiles}</th>
                  <th>{t.admin.smtmIssues}</th>
                </tr>
              </thead>
              <tbody>
                {filteredModels.map((entry) => (
                  <tr key={entry.name}>
                    <td>
                      <div className="font-semibold text-text-primary">{entry.display_name || entry.name}</div>
                      <div className="text-xs text-text-muted break-all">{entry.name}</div>
                    </td>
                    <td>
                      <span className={`admin-status-pill ${smtmStateClass(entry.status)}`}>{smtmStateLabel(entry.status)}</span>
                    </td>
                    <td>{formatInteger(entry.blob_count || 0)}</td>
                    <td>{formatTimestamp(entry.latest_upload_at)}</td>
                    <td>
                      <div className="admin-azurite-files">
                        {(entry.files || []).map((file) => (
                          <a key={file.name} className="homepage-button-secondary admin-azurite-file" href={file.download_url} target="_blank" rel="noreferrer">
                            <span>{file.filename}</span>
                            <span>{formatBytes(file.size_bytes)}</span>
                          </a>
                        ))}
                      </div>
                    </td>
                    <td>
                      {(entry.issues || []).length ? (
                        <div className="admin-smtm-issue-list">
                          {entry.issues.map((issue) => <span key={`${entry.name}-${issue}`} className="admin-status-pill is-warning">{smtmIssueLabel(issue)}</span>)}
                        </div>
                      ) : <span className="admin-status-pill is-ok">{t.admin.smtmNoIssues}</span>}
                    </td>
                  </tr>
                ))}
                {!filteredModels.length ? (
                  <tr><td colSpan="6">{t.admin.azuriteNoModels}</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AzuritePage;
